<template>
  <nav
    id="vue-the-menu"
    :class="
      bemBlockMod({
        closed: isFloatingMenuClosed,
      })
    "
    :style="cssCustomProps"
    @mouseleave="isBreakpointDesktop && toggleSelectedCategory()"
  >
    <div
      v-if="isFloatingMenuOpen"
      :class="bemElement('backdrop')"
      @touchstart.prevent="handleCloseMenu"
    />
    <div
      :class="
        bemElement('inner', {
          'is-clip-path': !isZoomed,
        })
      "
    >
      <div
        :class="bemElement('bar')"
        @click="handleOpenMenuClick"
        @click.prevent="handleToggleMenu"
      >
        <div v-show="shouldShowLabel" ref="title" :class="bemElement('title')">
          <span>{{ $msg('menu.title', 'header') }}</span>
        </div>
        <button
          :class="
            bemElement('floating-button', {
              closed: isFloatingMenuClosed,
            })
          "
        >
          <span :class="bemElement('icon', 'burger')" />
        </button>
      </div>
      <button
        :class="[bemElement('btn-close'), 'btn-close']"
        @click="handleCloseMenuClick"
        @click.prevent="handleCloseMenu"
      />
      <div :class="bemElement('layer', { open: !isClosedMenu })">
        <div ref="wrapper" :class="bemElement('wrapper')">
          <ul
            ref="list"
            :class="bemElement('list', { overflowing: overflowing })"
          >
            <TheMenuRawItem
              v-if="!searchAIEnabled"
              bem="the-menu-search-item"
              :is-fixed="true"
              :has-separator-after="true"
              @click="handleSearchClick"
            >
              <i class="icon-search" /> {{ $msg('search.menu.link', 'search') }}
            </TheMenuRawItem>
            <TheMenuItem
              v-for="(category, index) in categories"
              :key="index"
              :category="category"
              :inherited-bg-color="menu.main.categoryBgColor"
              :is-fixed="menu.attach_category_id === category.id"
              :is-submenu-open="categoryOpen === category.id"
              @menu:category-open="toggleSelectedCategory"
              @menu:update-height="updateMenuHeight"
            />
          </ul>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState } from 'vuex';
import { clearBodyLocks, lock, unlock } from 'tua-body-scroll-lock';
import { types } from '../store';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import TheMenuItem from './TheMenuItem.vue';
import TheMenuRawItem from './TheMenuRawItem.vue';
import { isIOSUserAgent } from '../../common/utils/device';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import EventBusConfig from '../config/eventBusConfig';
import { pickHighestContrastColor } from '../../common/utils/contrastColor';

const analyticsEventBus = getAnalyticsEventBus();
const MENU_HEIGHT_MOBILE = 50;
const FRONT_CONFIG = getFrontConfig();

export default {
  name: 'TheMenu',

  components: {
    TheMenuItem,
    TheMenuRawItem,
  },

  mixins: [bemClassesMixin, windowResizeMixin],

  props: {
    menu: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    categoryOpen: null,
    dataChangeBreakpoint: FRONT_CONFIG.responsive.bp_1025,
    labelChangeBreakpoint: FRONT_CONFIG.responsive.bp_575,
    overflowing: false,
    isZoomed: false,
    labelWidth: 0,
    listHeight: 0,
    menuHeightMobile: MENU_HEIGHT_MOBILE,
    menuPosition: 0,
    pageType: '',
    searchAIEnabled: FRONT_CONFIG.searchAI.isEnabledInFront,
  }),

  computed: {
    ...mapState({
      isClosedMenu: (state) => state.menu.isClosedMenu,
      menuCategoriesHeight: (state) => state.menu.menuCategoriesHeight,
      blockRenderType: (state) => state.menu.blockRenderType,
      activeSubmenuBgColor: (state) => state.menu.activeSubmenuBgColor,
    }),
    closeButtonColor() {
      const backgroundColor =
        this.activeSubmenuBgColor ??
        this.menu.main.categoryBgColor ??
        '#FFFFFF';
      return pickHighestContrastColor(backgroundColor, [
        this.menu.main.closeButtonDarkColor,
        this.menu.main.closeButtonLightColor,
      ]);
    },
    isFloatingMenuClosed() {
      return this.isClosedMenu && this.isBreakpointMobile;
    },
    isFloatingMenuOpen() {
      return !this.isClosedMenu && this.isBreakpointMobile;
    },
    cssCustomProps() {
      return {
        '--category-bg-color': this.menu.main.categoryBgColor ?? null,
        '--category-color': this.menu.main.categoryColor ?? null,
        '--category-color-desktop': this.menu.main.categoryColor ?? null,
        '--menu-bg-color': this.menu.main.menuBgColor ?? null,
        '--menu-icon-color': this.menu.main.menuIconColor ?? null,
        '--menu-close-btn-color': this.closeButtonColor,
        '--menu-height': `${this.menuTotalHeight}px` ?? null,
        '--menu-label-width': `${this.labelWidth ?? 0}px`,
        '--menu-position': `${this.menuPosition}px`,
      };
    },
    categories() {
      return this.menu.categories.reduce((acc, category) => {
        if (
          category.byl_renderType === this.blockRenderType &&
          ((this.isBreakpointMobile && !category.byl_displayMenu) ||
            (!this.isBreakpointMobile && !category.byl_displayMenuDesktop))
        ) {
          return acc;
        }
        return [...acc, category];
      }, []);
    },
    isLabeled() {
      return (
        !this.menu.label_excluded?.some(
          (pageType) => pageType.displayValue === this.pageType
        ) || this.$windowWidth >= this.labelChangeBreakpoint
      );
    },
    labelDefinition() {
      return (
        !!this.menu.page_types &&
        this.menu.page_types.length > 0 &&
        !!this.menu.label_excluded &&
        this.menu.label_excluded.length > 0
      );
    },
    menuTotalHeight() {
      return this.menuHeightMobile + this.menuCategoriesHeight;
    },
    scrollableElements() {
      const elements = Array.from(
        document.querySelectorAll('.the-menu-submenu__wrapper')
      );
      elements.push(this.$refs.wrapper);
      return elements;
    },
    shouldShowLabel() {
      return this.isLabeled && this.labelDefinition;
    },
  },

  watch: {
    isClosedMenu() {
      if (this.isClosedMenu) {
        this.toggleSelectedCategory();
        this.releaseBodyScroll();
      }
    },
    isLabeled() {
      this.$nextTick(() => {
        this.calculateLabelWidth();
      });
    },
    $route(newRoute) {
      const currentPageType = this.menu.page_types.find(
        (pageType) => pageType.value === newRoute.name
      );
      this.pageType = currentPageType
        ? currentPageType.displayValue
        : window?.gaInfo?.pageType;
    },
    $windowWidth() {
      this.onResize();
      this.$nextTick(() => {
        this.isOverflowing();
        this.calculateMenuPosition();
      });
    },
  },

  mounted() {
    this.pageType = window?.gaInfo?.pageType;
    this.updateListHeight();
    this.$store.commit(
      types.menu.setGroupRenderType,
      this.menu.group_render_type
    );
    this.$store.commit(
      types.menu.setBlockRenderType,
      this.menu.block_render_type
    );
    this.$store.commit(types.menu.setBackButtonColors, {
      dark: this.menu.main.backButtonDarkColor,
      light: this.menu.main.backButtonLightColor,
    });

    // check for resize visualViewport
    window.visualViewport.addEventListener(
      'resize',
      this.onResizeVisualVieportHandler
    );

    this.$nextTick(() => {
      this.isOverflowing();
      this.calculateLabelWidth();
      this.calculateMenuPosition();
    });
  },

  beforeDestroy() {
    // remove resize event Set on visualViewport
    window.visualViewport.removeEventListener(
      'resize',
      this.onResizeVisualVieportHandler
    );
    document.body.classList.remove('header-popup-open');
    // clear all possible body locks from tua-body-scroll-lock
    if (isIOSUserAgent()) {
      clearBodyLocks();
    }
  },

  methods: {
    onResizeVisualVieportHandler() {
      // pinch-in or pinch-out causes visualViewport scale to change, then try close menu
      if (window.visualViewport.scale !== 1) {
        if (this.isBreakpointMobile) {
          this.handleCloseMenu();
        }
        this.isZoomed = true;
      } else {
        this.isZoomed = false;
      }
    },
    onResize() {
      this.updateListHeight();
      this.handleCloseMenu();
    },
    calculateLabelWidth() {
      this.labelWidth = this.shouldShowLabel
        ? this.$refs.title?.clientWidth
        : 0;
    },
    calculateMenuPosition() {
      this.menuPosition = this.$refs.list.getBoundingClientRect().left;
    },
    handleCloseMenu() {
      this.releaseBodyScroll();
      this.toggleSelectedCategory();
      this.$store.dispatch(types.menu.setMenuState, {});
    },
    handleCloseMenuClick() {
      // Send info for Analytics
      analyticsEventBus.publish(analyticsEventBus.EVENTS.menuCloseIconClicked);
    },
    handleOpenMenuClick() {
      // Send info for Analytics
      analyticsEventBus.publish(analyticsEventBus.EVENTS.menuOpenIconClicked);
    },
    handleSearchClick() {
      this.$eventBus.$emit(EventBusConfig.commands.TheSearch.show);
      this.handleCloseMenu();
    },
    handleToggleMenu() {
      this.toggleSelectedCategory();
      const isClosed = !this.isClosedMenu;
      const categoriesHeight = isClosed ? 0 : this.listHeight;
      this.$store.dispatch(types.menu.setMenuState, {
        isClosed,
        categoriesHeight,
      });
      if (isClosed) {
        this.releaseBodyScroll();
      } else {
        this.lockBodyScroll();
      }
    },
    isOverflowing() {
      this.overflowing = this.isBreakpointMobile
        ? this.$refs.list.scrollWidth > this.$refs.list.clientWidth
        : false;
    },
    lockBodyScroll() {
      document.body.classList.add('header-popup-open');
      if (isIOSUserAgent()) {
        lock(this.scrollableElements);
      }
    },
    releaseBodyScroll() {
      document.body.classList.remove('header-popup-open');
      if (isIOSUserAgent()) {
        unlock(this.scrollableElements);
      }
    },
    toggleSelectedCategory(categoryId = null) {
      this.categoryOpen = this.categoryOpen !== categoryId ? categoryId : null;
      const openedCategory = this.categoryOpen
        ? this.categories.find((category) => category.id === this.categoryOpen)
        : null;
      this.$store.commit(
        types.menu.setActiveSubmenuBgColor,
        openedCategory?.subcategoryBgColor ?? this.menu.main.categoryBgColor
      );
    },
    updateListHeight() {
      this.listHeight = this.$refs.list.getBoundingClientRect().height || 0;
    },
    updateMenuHeight() {
      this.updateListHeight();
      this.$store.commit(types.menu.setMenuCategoriesHeight, this.listHeight);
      this.toggleSelectedCategory();
    },
  },
};
</script>
